import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { RuleBuilder, buildRule, parseRule } from "@/components/RuleBuilder";
import { ConvertToDynamicDialog } from "@/components/ConvertToDynamicDialog";
import { Boxes, Lock, Globe, Search, Plus, Pencil, Trash2, Users2, MessagesSquare, X, Sparkles, ArrowRightLeft, Eye, EyeOff } from "lucide-react";

const isPublic = (g) => (g.visibility || "").toLowerCase() === "public";

// --- reusable people picker (search + add) ---
function PeoplePicker({ users, exclude, onPick, placeholder, testid }) {
  const [q, setQ] = useState("");
  const opts = useMemo(() => {
    const ex = new Set(exclude || []);
    const s = q.toLowerCase();
    return users.filter((u) => !ex.has(u.id) &&
      `${u.displayName} ${u.userPrincipalName || u.mail || ""}`.toLowerCase().includes(s)).slice(0, 6);
  }, [users, q, exclude]);
  return (
    <div className="relative">
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="rounded-sm border-zinc-300 pl-8 text-sm" />
      {q && opts.length > 0 && (
        <div className="absolute z-20 mt-1 w-full border border-zinc-200 bg-white shadow-lg">
          {opts.map((u) => (
            <button key={u.id} onClick={() => { onPick(u); setQ(""); }} data-testid={`pick-${u.id}`}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-zinc-50">
              <span className="font-medium text-zinc-800">{u.displayName}</span>
              <span className="text-xs text-zinc-400">{u.userPrincipalName || u.mail}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function M365Groups() {
  const { can, isAdmin } = useAuth();
  const canManage = can("groups.manage");
  const { t } = useLang();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);       // {mode, group}
  const [del, setDel] = useState(null);
  const [teamify, setTeamify] = useState(null);
  const [manage, setManage] = useState(null);   // group detail for members drawer
  const [convert, setConvert] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); return api.get("/m365-groups").then((r) => setGroups(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false)); };
  useEffect(() => {
    load();
    if (canManage) api.get("/users").then((r) => setUsers((r.data || []).filter((u) => !u.isShared))).catch(() => {});
  }, [canManage]);

  const rows = useMemo(() => groups.filter((g) =>
    `${g.displayName} ${g.description || ""} ${g.mail || ""}`.toLowerCase().includes(q.toLowerCase())), [groups, q]);

  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/m365-groups/${del.id}`); toast.success(t("m365.deleted")); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const doTeamify = async () => {
    setBusy(true);
    try { await api.post(`/m365-groups/${teamify.id}/teamify`); toast.success(t("m365.teamified")); setTeamify(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const toggleVis = async (gid, isVisible) => {
    setGroups((gs) => gs.map((x) => x.id === gid ? { ...x, isVisible } : x));
    try { await api.patch(`/groups/${gid}/visibility`, { isVisible }); }
    catch (e) { toast.error(apiErr(e)); setGroups((gs) => gs.map((x) => x.id === gid ? { ...x, isVisible: !isVisible } : x)); }
  };

  const VisBadge = ({ g }) => (
    <span className="inline-flex items-center gap-1.5 border border-zinc-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
      {isPublic(g) ? <Globe size={12} /> : <Lock size={12} />} {isPublic(g) ? t("m365.public") : t("m365.private")}
    </span>
  );

  return (
    <>
      <PageHeader title={t("m365.title")} subtitle={`${rows.length} ${t("m365.subtitle")}`}>
        {canManage && (
          <Button onClick={() => setForm({ mode: "create", group: { visibility: "Private", owners: [], members: [] } })}
            data-testid="m365-create-button" className="rounded-sm bg-primary hover:bg-[#002480]">
            <Plus size={16} className="mr-2" /> {t("m365.create")}
          </Button>
        )}
      </PageHeader>
      <div className="p-6 md:p-10">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("m365.searchPh")} data-testid="m365-search" className="rounded-sm border-zinc-300 pl-9" />
          </div>
        </div>

        {loading ? (
          <div className="border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-400">…</div>
        ) : rows.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-400" data-testid="m365-empty">{t("m365.empty")}</div>
        ) : (
          <div className="border border-zinc-200 bg-white">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                  <th className="px-4 py-3">{t("m365.colName")}</th>
                  <th className="hidden px-4 py-3 lg:table-cell">{t("m365.colAlias")}</th>
                  <th className="hidden px-4 py-3 md:table-cell">{t("m365.colVisibility")}</th>
                  <th className="px-4 py-3">{t("m365.colOwners")}</th>
                  <th className="px-4 py-3">{t("m365.colMembers")}</th>
                  {isAdmin && <th className="px-4 py-3">{t("groups.colVisible")}</th>}
                  {canManage && <th className="px-4 py-3 text-right">{t("m365.colActions")}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => (
                  <tr key={g.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`m365-row-${g.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center bg-primary/10 text-primary"><Boxes size={16} /></span>
                        <div><div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">{g.displayName}{g.dynamic && <span data-testid={`m365-dynamic-${g.id}`} className="border border-violet-200 bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700">{t("groups.dynamic")}</span>}</div><div className="text-xs text-zinc-400">{g.description}</div></div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 text-xs text-zinc-500 lg:table-cell">{g.mail || g.mailNickname || "—"}</td>
                    <td className="hidden px-4 py-3 md:table-cell"><VisBadge g={g} /></td>
                    <td className="px-4 py-3 font-display text-lg font-black text-zinc-900">{g.ownerCount ?? 0}</td>
                    <td className="px-4 py-3 font-display text-lg font-black text-zinc-900">{g.memberCount ?? 0}</td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <button onClick={() => toggleVis(g.id, g.isVisible === false)} data-testid={`m365-vis-${g.id}`}
                          title={g.isVisible === false ? t("groups.toggleHidden") : t("groups.toggleVisible")}
                          className={`flex h-8 w-8 items-center justify-center border transition-colors ${g.isVisible === false ? "border-zinc-200 bg-zinc-50 text-zinc-300 hover:text-zinc-500" : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
                          {g.isVisible === false ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </td>
                    )}
                    {canManage && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setManage(g)} data-testid={`m365-members-${g.id}`} title={t("m365.members")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-zinc-100 hover:text-primary"><Users2 size={14} /></button>
                          {!g.dynamic && <button onClick={() => setConvert(g)} data-testid={`m365-convert-${g.id}`} title={t("convert.action")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-violet-50 hover:text-violet-600"><ArrowRightLeft size={14} /></button>}
                          <button onClick={() => setForm({ mode: "edit", group: g })} data-testid={`m365-edit-${g.id}`} title={t("m365.edit")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-zinc-100 hover:text-primary"><Pencil size={14} /></button>
                          <button onClick={() => setTeamify(g)} data-testid={`m365-teamify-${g.id}`} title={t("m365.teamify")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-indigo-50 hover:text-indigo-600"><MessagesSquare size={14} /></button>
                          <button onClick={() => setDel(g)} data-testid={`m365-delete-${g.id}`} title={t("m365.delete")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {form && <GroupFormDialog form={form} users={users} onClose={() => setForm(null)} onSaved={load} t={t} />}
      {manage && <MembersDialog group={manage} users={users} onClose={() => setManage(null)} onChanged={load} t={t} />}
      {convert && <ConvertToDynamicDialog group={convert} apiBase="/m365-groups" onClose={() => setConvert(null)} onDone={() => { setConvert(null); load(); }} />}

      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="rounded-sm" data-testid="m365-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">{t("m365.deleteConfirm")}</DialogTitle>
            <DialogDescription className="text-xs">{t("m365.deleteWarn")} <strong>{del?.displayName}</strong><br /><span className="mt-2 block text-zinc-400">{t("m365.deleteSoftNote")}</span></DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={doDelete} disabled={busy} data-testid="m365-delete-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">{t("m365.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!teamify} onOpenChange={(o) => !o && setTeamify(null)}>
        <DialogContent className="rounded-sm" data-testid="m365-teamify-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">{t("m365.teamifyConfirm")}</DialogTitle>
            <DialogDescription className="text-xs">{t("m365.teamifyDesc")} <strong>{teamify?.displayName}</strong></DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamify(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={doTeamify} disabled={busy} data-testid="m365-teamify-confirm" className="rounded-sm bg-indigo-600 hover:bg-indigo-700">{t("m365.teamifyBtn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// --- create / edit dialog ---
function GroupFormDialog({ form, users, onClose, onSaved, t }) {
  const isEdit = form.mode === "edit";
  const g = form.group || {};
  const [displayName, setName] = useState(g.displayName || "");
  const [mailNickname, setAlias] = useState(g.mailNickname || "");
  const [aliasEdited, setAliasEdited] = useState(false);
  const onName = (v) => {
    setName(v);
    if (!isEdit && !aliasEdited) setAlias(v.replace(/[^a-zA-Z0-9._-]/g, "").slice(0, 64));
  };
  const [description, setDesc] = useState(g.description || "");
  const [visibility, setVis] = useState(g.visibility || "Private");
  const [membershipType, setMType] = useState(g.membershipType || "assigned");
  const [membershipRule, setRule] = useState(g.membershipRule || "");
  const [hiddenFromGal, setHiddenGal] = useState(!!g.hiddenFromGal);
  const [owners, setOwners] = useState([]);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const uMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const isDynamic = membershipType === "dynamic";

  const submit = async () => {
    if (!displayName.trim()) { toast.error(t("m365.needName")); return; }
    if (!isEdit && !/^[a-zA-Z0-9._-]{1,64}$/.test(mailNickname.trim())) { toast.error(t("m365.needAlias")); return; }
    if (isDynamic && parseRule(membershipRule).clauses.length === 0) { toast.error(t("m365.needRule")); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.patch(`/m365-groups/${g.id}`, { displayName, description, visibility, membershipType, membershipRule: isDynamic ? membershipRule : "", hiddenFromGal }); toast.success(t("m365.updated")); }
      else { await api.post(`/m365-groups`, { displayName, mailNickname, description, visibility, owners, members: isDynamic ? [] : members, membershipType, membershipRule: isDynamic ? membershipRule : "", hiddenFromGal }); toast.success(t("m365.created")); }
      onClose(); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const Chip = ({ id, onRemove }) => (
    <span className="inline-flex items-center gap-1 border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs">
      {uMap[id]?.displayName || id}
      <button onClick={onRemove} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
    </span>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="m365-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{isEdit ? t("m365.editTitle") : t("m365.createTitle")}</DialogTitle>
          <DialogDescription className="text-xs">{isEdit ? t("m365.editDesc") : t("m365.createDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("m365.fieldName")}</label>
            <Input value={displayName} onChange={(e) => onName(e.target.value)} data-testid="m365-field-name" className="rounded-sm border-zinc-300" />
          </div>
          {!isEdit && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("m365.fieldAlias")}</label>
              <Input value={mailNickname} onChange={(e) => { setAlias(e.target.value); setAliasEdited(true); }} data-testid="m365-field-alias" className="rounded-sm border-zinc-300" />
              <p className="mt-1 text-[11px] text-zinc-400">{t("m365.fieldAliasHint")}</p>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("m365.fieldDescription")}</label>
            <Textarea value={description} onChange={(e) => setDesc(e.target.value)} data-testid="m365-field-desc" className="rounded-sm border-zinc-300" rows={2} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("m365.fieldVisibility")}</label>
            <Select value={visibility} onValueChange={setVis}>
              <SelectTrigger data-testid="m365-field-visibility" className="rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Private">{t("m365.private")}</SelectItem>
                <SelectItem value="Public">{t("m365.public")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("m365.membershipType")}</label>
            <Select value={membershipType} onValueChange={setMType}>
              <SelectTrigger data-testid="m365-field-membershiptype" className="rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assigned">{t("m365.assigned")}</SelectItem>
                <SelectItem value="dynamic">{t("m365.dynamicType")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isDynamic && (
            <div className="border border-violet-200 bg-violet-50/40 p-3">
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-violet-700"><Sparkles size={13} /> {t("m365.dynamicNote")}</div>
              <RuleBuilder value={membershipRule} onChange={setRule} testid="m365-rule" preview templates />
            </div>
          )}
          <div className="flex items-center justify-between border border-zinc-200 px-3 py-2.5">
            <div className="pr-3">
              <div className="text-sm font-medium text-zinc-800">{t("gal.hide")}</div>
              <div className="text-xs text-zinc-400">{t("gal.hideHelp")}</div>
            </div>
            <Switch checked={hiddenFromGal} onCheckedChange={setHiddenGal} data-testid="m365-field-hidegal" />
          </div>
          {!isEdit && !isDynamic && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("m365.owners")}</label>
                <PeoplePicker users={users} exclude={owners} onPick={(u) => setOwners((o) => [...o, u.id])} placeholder={t("m365.searchPeople")} testid="m365-owner-picker" />
                <div className="mt-2 flex flex-wrap gap-1.5">{owners.map((id) => <Chip key={id} id={id} onRemove={() => setOwners((o) => o.filter((x) => x !== id))} />)}</div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("m365.membersField")}</label>
                <PeoplePicker users={users} exclude={members} onPick={(u) => setMembers((m) => [...m, u.id])} placeholder={t("m365.searchPeople")} testid="m365-member-picker" />
                <div className="mt-2 flex flex-wrap gap-1.5">{members.map((id) => <Chip key={id} id={id} onRemove={() => setMembers((m) => m.filter((x) => x !== id))} />)}</div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="m365-form-submit" className="rounded-sm bg-primary hover:bg-[#002480]">{isEdit ? t("m365.save") : t("m365.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- members & owners management ---
function MembersDialog({ group, users, onClose, onChanged, t }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/m365-groups/${group.id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [group.id]);

  const act = async (fn) => { setBusy(true); try { await fn(); await load(); onChanged(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const addOwner = (u) => act(() => api.post(`/m365-groups/${group.id}/owners`, { userId: u.id }));
  const removeOwner = (id) => act(() => api.delete(`/m365-groups/${group.id}/owners/${id}`));
  const addMember = (u) => act(() => api.post(`/m365-groups/${group.id}/members`, { userId: u.id }));
  const removeMember = (id) => act(() => api.delete(`/m365-groups/${group.id}/members/${id}`));

  const owners = detail?.owners || [];
  const members = detail?.members || [];
  const isDynamic = (detail?.dynamic ?? group.dynamic) === true;
  const Row = ({ p, onRemove, canRemove, testid }) => (
    <div className="flex items-center justify-between border-b border-zinc-100 py-2 last:border-0">
      <div><div className="text-sm font-medium text-zinc-800">{p.displayName}</div><div className="text-xs text-zinc-400">{p.userPrincipalName || p.mail}</div></div>
      <button onClick={onRemove} disabled={busy || !canRemove} data-testid={testid} title={!canRemove ? t("m365.removeLastOwner") : ""}
        className="flex h-7 w-7 items-center justify-center border border-zinc-200 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"><X size={13} /></button>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="m365-members-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{t("m365.membersTitle")} — {group.displayName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("m365.ownersList")} ({owners.length})</div>
            <PeoplePicker users={users} exclude={owners.map((o) => o.id)} onPick={addOwner} placeholder={t("m365.addOwner")} testid="m365-add-owner" />
            <div className="mt-2">{owners.length === 0 ? <p className="text-xs text-zinc-400">{t("m365.noOwners")}</p> :
              owners.map((p) => <Row key={p.id} p={p} onRemove={() => removeOwner(p.id)} canRemove={owners.length > 1} testid={`m365-remove-owner-${p.id}`} />)}</div>
          </div>
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("m365.membersList")} ({members.length})</div>
            {isDynamic ? (
              <p className="mb-2 border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] text-violet-700" data-testid="m365-dynamic-members-note">{t("sg.dynamicMembersNote")}</p>
            ) : (
              <PeoplePicker users={users} exclude={members.map((m) => m.id)} onPick={addMember} placeholder={t("m365.addMember")} testid="m365-add-member" />
            )}
            <div className="mt-2">{members.length === 0 ? <p className="text-xs text-zinc-400">{t("m365.noMembers")}</p> :
              members.map((p) => <Row key={p.id} p={p} onRemove={() => removeMember(p.id)} canRemove={!isDynamic} testid={`m365-remove-member-${p.id}`} />)}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">{t("common.close") || t("common.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
